/**
 * Binary wire codec (WebSocket frames). Two directions, two encodings:
 *
 *  server → client  `encode` / `decode` — msgpackr with
 *    - `moreTypes`: typed arrays (Uint8Array, Int16Array, Float32Array …) travel as msgpackr's
 *      typed-array extension and decode to the SAME class with exact contents. Without it a
 *      Node decoder returns Int16Array data as a raw Buffer of bytes and `Buffer.slice()` aliases
 *      memory — both silently corrupt views.
 *    - records (default on): repeated object shapes (units, contacts …) send their keys once per
 *      message, roughly halving frame size. No shared structures and no `sequential` mode, so every
 *      frame decodes on its own (safe across reconnects).
 *    - `mapsAsObjects`: plain msgpack maps from other encoders decode to objects, not `Map`s.
 *
 *  client → server  `encodeClientMessage` / `decodeClientMessage` — plain, standard MessagePack
 *    (no records, no extensions) decoded by the strict decoder in strict-msgpack.ts and then
 *    validated with zod. The server never runs msgpackr's decoder on untrusted bytes.
 */
import { Packr, Unpackr } from 'msgpackr';
import type { ClientMessage, ServerMessage } from './messages';
import { decodeStrict } from './strict-msgpack';
import { parseClientMessage } from './validate';

const RICH = { moreTypes: true, mapsAsObjects: true } as const;

// Separate instances: a Packr is also an Unpackr, and sharing one would let record structures
// learned while encoding leak into decoding.
const packr = new Packr(RICH);
const unpackr = new Unpackr(RICH);
/**
 * Standard MessagePack only. `undefined` (optional command fields) is written as msgpackr's
 * fixext1 type 0, the single extension the strict decoder accepts.
 */
const plainPackr = new Packr({ useRecords: false });

/**
 * Frames are views onto ordinary (non-shared) ArrayBuffers, which is what `WebSocket.send`
 * requires in current DOM typings; msgpackr's `Buffer` return type does not say so.
 */
export type Frame = Uint8Array<ArrayBuffer>;

/** Encode a message (typically ServerMessage) for the wire. */
export function encode(msg: ServerMessage | ClientMessage): Frame {
  return packr.pack(msg) as Frame;
}

/** Decode a frame produced by `encode` (or any standard MessagePack). Throws on malformed data. */
export function decode(data: Uint8Array | ArrayBuffer): unknown {
  return unpackr.unpack(data instanceof ArrayBuffer ? new Uint8Array(data) : data);
}

/** Encode a ClientMessage as plain MessagePack (what the server's strict decoder accepts). */
export function encodeClientMessage(msg: ClientMessage): Frame {
  return plainPackr.pack(msg) as Frame;
}

/**
 * Decode + validate an untrusted client frame. Returns null for anything that is not a
 * well-formed, in-bounds ClientMessage — never throws.
 */
export function decodeClientMessage(data: Uint8Array | ArrayBuffer): ClientMessage | null {
  let raw: unknown;
  try {
    raw = decodeStrict(data instanceof ArrayBuffer ? new Uint8Array(data) : data);
  } catch {
    return null;
  }
  return parseClientMessage(raw);
}
